import { useEffect } from 'react';
import { X } from 'lucide-react';
import { StoryBody } from '@/components/story/StoryBody';
import { ParentInfoBox } from '@/components/story/StorySBKurzerklaert';
import { CatalogInlineAudioPlayer } from '@/components/catalog/CatalogInlineAudioPlayer';
import { useUiStore } from '@/stores/uiStore';
import { STRINGS } from '@/strings/strings';
import type { CatalogReadContent } from '@/features/catalog/types';

interface Props {
  content: CatalogReadContent;
  onClose: () => void;
}

/**
 * Catalog lesen view. Replicates the SB-Sachgeschichte Story-Output chrome
 * (StoryRoute's internal SBSachgeschichteStoryOutputContent) WITHOUT touching
 * StoryRoute — the live story output stays byte-identical. Reuses the public
 * pieces: bg-world-sb full-bleed page, centered fox mascot, the
 * "📖 Schlauberger Wissensgeschichten" Welt-Label, the bg-dream-light-blue
 * card, StoryBody, ParentInfoBox.
 *
 * Differences from the story page (by design): no StoryHeader / favorite /
 * share / audio / rating / footer (those are story-specific), and no date line
 * (catalog read content carries no date). Page-level scroll + the story
 * chrome's generous bottom padding replace the prior fixed overlay — which is
 * what fixes the desktop bottom cutoff.
 */
export function CatalogReaderView({ content, onClose }: Props) {
  const weltLabel = STRINGS.storyOutput.page.weltLabels.sbSachgeschichte;
  const S = STRINGS.catalog.detail;

  // While this in-place reader is open, suppress the global MiniPlayer (the
  // reader hosts its own top-mounted player) — AppShell reads this flag.
  // Cleared on close / navigation away so the MiniPlayer reappears with the
  // catalog track still playing.
  const setCatalogReaderOpen = useUiStore((s) => s.setCatalogReaderOpen);
  useEffect(() => {
    setCatalogReaderOpen(true);
    return () => setCatalogReaderOpen(false);
  }, [setCatalogReaderOpen]);

  return (
    <div className="relative -mb-[calc(10rem+env(safe-area-inset-bottom,0))] min-h-screen overflow-hidden bg-world-sb pb-[calc(10rem+env(safe-area-inset-bottom,0))]">
      <div className="relative mx-auto max-w-[560px] px-5 pt-6 pb-12 md:max-w-[768px] desktop:max-w-[1100px]">
        <button
          type="button"
          onClick={onClose}
          className="mb-2 flex items-center gap-1 text-sm text-dream-navy/70 hover:text-dream-navy"
        >
          <X aria-hidden className="h-4 w-4" />
          {S.readClose}
        </button>

        {/* Relocated player (Möglichkeit 3): the existing catalog player mounts
            at the TOP of the reader page (self-gated on kind==='catalog', so it
            shows only while a Folge is playing — i.e. for anhören, not lesen). */}
        <CatalogInlineAudioPlayer />

        <div className="mt-3 flex justify-center">
          <img
            src="/worlds/sb/fox.png"
            alt=""
            aria-hidden="true"
            className="block h-[120px] w-auto md:h-[180px] desktop:h-[240px]"
          />
        </div>

        <div className="mt-5 rounded-xl bg-dream-light-blue p-5">
          <p className="text-xs font-bold uppercase tracking-[1px] text-sb-welt-label">
            {weltLabel}
          </p>
          {content.title && (
            <h2 className="mt-3 text-2xl font-bold leading-tight text-dream-navy break-words">
              {content.title}
            </h2>
          )}
          <div className="mx-auto mt-4 max-w-[640px]">
            <StoryBody storyText={content.storyText} storyTextPages={null} />
          </div>
          <div className="mt-6">
            <ParentInfoBox parentBox={content.parentText} citations={content.citations} />
          </div>
        </div>
      </div>
    </div>
  );
}
